import type { Aggregation, Market, MarketAccount } from '@talos/kyoko';
import {
  AggregationIcon,
  Box,
  Button,
  Flex,
  FormControlSizes,
  HelpIcon,
  HStack,
  IndicatorBadge,
  Popover,
  Stack,
  usePopoverState,
  type BoxProps,
} from '@talos/kyoko';
import type { ReactNode } from 'react';
import { memo, useCallback, useMemo, useRef } from 'react';
import { OverflowList } from 'react-overflow-list';
import styled from 'styled-components';

type InnerSelectedMarketsDisplayProps = {
  aggregation: Aggregation | undefined;
  markets: Market[] | undefined;
  marketAccounts: MarketAccount[] | undefined;
  maxVisibleItems?: number;
  overflowTrigger?: 'button' | 'badge';
  placeholder?: ReactNode;
  tooltip?: ReactNode;
} & BoxProps;

export const InnerSelectedMarketsDisplay = memo(function InnerSelectedMarketsDisplay({
  aggregation,
  markets,
  marketAccounts,
  maxVisibleItems,
  overflowTrigger = 'button',
  placeholder,
  tooltip,
  ...boxProps
}: InnerSelectedMarketsDisplayProps) {
  const overflowRef = useRef(0);

  const combined = useMemo(() => [...(marketAccounts ?? []), ...(markets ?? [])], [markets, marketAccounts]);
  // Split items into:
  // 1) items eligible for inline rendering, and
  // 2) items that must always be treated as overflow due to maxVisibleItems.
  // OverflowList may hide additional inline items based on available width.
  const itemsToRender = useMemo(() => {
    if (maxVisibleItems == null) {
      return combined;
    }
    return combined.slice(0, maxVisibleItems);
  }, [combined, maxVisibleItems]);
  const forcedOverflowItems = useMemo(() => {
    if (maxVisibleItems == null) {
      return [];
    }
    return combined.slice(maxVisibleItems);
  }, [combined, maxVisibleItems]);
  const itemRenderer = useCallback(
    (item: Market | MarketAccount, index: number) =>
      `${item.DisplayName}${index < itemsToRender.length - overflowRef.current - 1 ? ', ' : ''}`,
    [itemsToRender.length]
  );

  const popover = usePopoverState({
    trigger: 'hover',
    placement: 'bottom-end',
    closeOnClickOutside: true,
    usePortal: true,
  });

  const overflowRenderer = useCallback(
    (overflowItems: (Market | MarketAccount)[]) => {
      overflowRef.current = overflowItems.length;
      const overflowItemsToRender = [...overflowItems, ...forcedOverflowItems];

      if (overflowItemsToRender.length === 0) {
        return null;
      }

      return (
        <Box>
          <Popover {...popover}>
            {overflowTrigger === 'badge' ? (
              <IndicatorBadge>+{overflowItemsToRender.length}</IndicatorBadge>
            ) : (
              <Button size={FormControlSizes.Small}>+{overflowItemsToRender.length}</Button>
            )}
            <Stack gap="spacing4">
              {overflowItemsToRender.map(item => (
                <div key={item.Name}>{item.DisplayName}</div>
              ))}
            </Stack>
          </Popover>
        </Box>
      );
    },
    [forcedOverflowItems, overflowTrigger, popover]
  );

  if (!aggregation && !markets?.length && !marketAccounts?.length) {
    return (
      <HStack
        overflow="hidden"
        flex="0 1 auto"
        fontSize="fontSizeSmall"
        gap={tooltip ? 'spacing4' : undefined}
        {...boxProps}
      >
        {tooltip ? <HelpIcon tooltip={tooltip} /> : null}
        <Box lineHeight={1} overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
          {placeholder ? placeholder : 'No selected markets'}
        </Box>
      </HStack>
    );
  }

  if (aggregation) {
    return (
      <Flex gap="spacing4" overflow="hidden" alignItems="center" {...boxProps}>
        <AggregationIcon />
        <Box fontSize="fontSizeSmall" overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap" flex="1 1 auto">
          {aggregation.DisplayName}
        </Box>
      </Flex>
    );
  }

  if (markets || marketAccounts) {
    return (
      <MarketsDisplayWrapper {...boxProps}>
        <OverflowList
          className="selected-markets-display"
          items={itemsToRender}
          itemRenderer={itemRenderer}
          overflowRenderer={overflowRenderer}
          minVisibleItems={0}
          collapseFrom="end"
          alwaysRenderOverflow={forcedOverflowItems.length > 0}
        />
      </MarketsDisplayWrapper>
    );
  }
});

const MarketsDisplayWrapper = styled(Flex)`
  .selected-markets-display {
    align-items: center;
    gap: ${({ theme }) => theme.spacing4}px;
  }
`;
MarketsDisplayWrapper.defaultProps = {
  justifyContent: 'start',
  gap: 'spacing4',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  flex: '1 1 0',
  fontSize: 'fontSizeSmall',
};
